import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, 
    faArrowRight, 
    faInfoCircle,
    faClock,
    faCheckCircle,
    faTimesCircle,
    faChevronDown,
    faChevronRight,
    faCheck,
    faBan
} from '@fortawesome/free-solid-svg-icons';
import { crmService } from '../../services/crmService';
import { useFlashMessage } from '../FlashMessage';
import { getErrorMessage } from '../../utils/errorMessages';
import './RFCDetailsModal.css';

const RFCDetailsModal = ({ isOpen, onClose, rfcList, profileName, isCRMApprover, profileId, onReviewSubmitted }) => {
    // Support both single RFC (backward compatibility) and list of RFCs
    const rfcArray = Array.isArray(rfcList) ? rfcList : (rfcList ? [rfcList] : []);
    
    // Track expanded state for each RFC (must be before any early returns)
    const [expandedRFCs, setExpandedRFCs] = useState(() => {
        // By default, expand only the first (newest) RFC
        return rfcArray.length > 0 ? { [rfcArray[0].id]: true } : {};
    });
    
    // Track field decisions for approval/rejection
    // Format: { rfcId: { fieldName: { decision: 'approved'|'rejected', rejection_reason: '' } } }
    const [fieldDecisions, setFieldDecisions] = useState({});
    
    // Track which RFC is currently being submitted
    const [isSubmittingReview, setIsSubmittingReview] = useState(null);
    
    const { showSuccess, showError } = useFlashMessage();
    
    // Early return AFTER hooks
    if (!isOpen || rfcArray.length === 0) return null;
    
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    
    const toggleRFC = (rfcId) => {
        setExpandedRFCs(prev => ({
            ...prev,
            [rfcId]: !prev[rfcId]
        }));
    };
    
    // Sort RFCs by created_at DESC (newest first)
    const sortedRFCs = [...rfcArray].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
    });
    
    // Build field ownership map for priority handling
    // When multiple RFCs modify the same field, the newest RFC takes priority
    const buildFieldOwnership = () => {
        const fieldOwnerMap = {}; // field_name -> RFC with highest priority (newest)
        
        // Process RFCs from oldest to newest, so newer RFCs overwrite
        [...sortedRFCs].reverse().forEach(rfc => {
            const changes = rfc.changes_data || {};
            Object.keys(changes).forEach(fieldName => {
                fieldOwnerMap[fieldName] = rfc.id;
            });
        });
        
        return fieldOwnerMap;
    };
    
    const fieldOwnership = buildFieldOwnership();
    
    // Filter changes for each RFC to only show fields it "owns"
    const getRelevantChanges = (rfc) => {
        const allChanges = rfc.changes_data || {};
        const relevantChanges = {};
        
        Object.entries(allChanges).forEach(([fieldName, change]) => {
            // Only include if this RFC owns this field
            if (fieldOwnership[fieldName] === rfc.id) {
                relevantChanges[fieldName] = change;
            }
        });
        
        return relevantChanges;
    };
    
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending':
                return faClock;
            case 'approved':
                return faCheckCircle;
            case 'rejected':
                return faTimesCircle;
            default:
                return faInfoCircle;
        }
    };
    
    const getStatusClass = (status) => {
        switch (status) {
            case 'pending':
                return 'rfc-status-pending';
            case 'approved':
                return 'rfc-status-approved';
            case 'rejected':
                return 'rfc-status-rejected';
            default:
                return 'rfc-status-pending';
        }
    };
    
    const formatFieldName = (fieldName) => {
        // Convert snake_case to Title Case
        return fieldName
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    };
    
    const formatNestedValue = (val) => {
        // Helper function to safely format nested values
        if (val === null || val === undefined || val === '') {
            return '(empty)';
        }
        if (typeof val === 'boolean') {
            return val ? 'Yes' : 'No';
        }
        if (typeof val === 'object') {
            // If it's an object, stringify it nicely
            if (val.formatted_address) {
                return val.formatted_address;
            }
            return JSON.stringify(val, null, 2);
        }
        return String(val);
    };
    
    // Helper to find differences between two arrays of objects
    const findArrayDifferences = (oldArray, newArray) => {
        if (!Array.isArray(oldArray) || !Array.isArray(newArray)) {
            return null;
        }
        
        const differences = [];
        
        // Try to match by ID or index
        newArray.forEach((newItem, idx) => {
            if (typeof newItem !== 'object' || newItem === null) return;
            
            // Find matching old item by id or index
            const itemId = newItem.id || newItem.Id;
            let oldItem = null;
            
            if (itemId) {
                oldItem = oldArray.find(old => (old.id || old.Id) === itemId);
            } else {
                oldItem = oldArray[idx];
            }
            
            if (!oldItem) {
                // New item added
                differences.push({
                    type: 'added',
                    index: idx + 1,
                    item: newItem,
                    changes: null
                });
            } else {
                // Check for changes
                const changes = {};
                Object.keys(newItem).forEach(key => {
                    if (JSON.stringify(newItem[key]) !== JSON.stringify(oldItem[key])) {
                        changes[key] = {
                            old: oldItem[key],
                            new: newItem[key]
                        };
                    }
                });
                
                if (Object.keys(changes).length > 0) {
                    differences.push({
                        type: 'modified',
                        index: idx + 1,
                        item: newItem,
                        changes: changes
                    });
                }
            }
        });
        
        // Check for removed items
        if (oldArray.length > newArray.length) {
            for (let i = newArray.length; i < oldArray.length; i++) {
                differences.push({
                    type: 'removed',
                    index: i + 1,
                    item: oldArray[i],
                    changes: null
                });
            }
        }
        
        return differences.length > 0 ? differences : null;
    };
    
    const formatValue = (value, oldValue = null) => {
        if (value === null || value === undefined) {
            return <span className="rfc-empty-value">(empty)</span>;
        }
        
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        
        if (Array.isArray(value)) {
            if (value.length === 0) {
                return <span className="rfc-empty-value">(empty)</span>;
            }
            
            // Check if array contains objects (like banking details)
            if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                // Try to find differences if we have old value
                const differences = oldValue ? findArrayDifferences(oldValue, value) : null;
                
                if (differences) {
                    // Show only what changed
                    return (
                        <div className="rfc-object-list">
                            {differences.map((diff, idx) => (
                                <div key={idx} className={`rfc-object-item ${diff.type === 'added' ? 'rfc-item-added' : diff.type === 'removed' ? 'rfc-item-removed' : ''}`}>
                                    <strong>
                                        Item {diff.index}
                                        {diff.type === 'added' && ' (NEW)'}
                                        {diff.type === 'removed' && ' (REMOVED)'}
                                        {diff.type === 'modified' && ' (Modified)'}:
                                    </strong>
                                    {diff.type === 'modified' && diff.changes && (
                                        <ul className="rfc-object-fields">
                                            {Object.entries(diff.changes).map(([key, change]) => (
                                                <li key={key}>
                                                    <span className="rfc-object-key">{formatFieldName(key)}:</span>{' '}
                                                    <span className="rfc-value-old-inline" style={{textDecoration: 'line-through', color: '#ef4444'}}>
                                                        {formatNestedValue(change.old)}
                                                    </span>
                                                    {' → '}
                                                    <span className="rfc-value-new-inline" style={{fontWeight: 600, color: '#10b981'}}>
                                                        {formatNestedValue(change.new)}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {(diff.type === 'added' || diff.type === 'removed') && (
                                        <ul className="rfc-object-fields">
                                            {Object.entries(diff.item).map(([key, val]) => (
                                                <li key={key}>
                                                    <span className="rfc-object-key">{formatFieldName(key)}:</span>{' '}
                                                    <span className="rfc-object-value">{formatNestedValue(val)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                }
                
                // Fallback: show all items
                return (
                    <div className="rfc-object-list">
                        {value.map((item, idx) => (
                            <div key={idx} className="rfc-object-item">
                                <strong>Item {idx + 1}:</strong>
                                <ul className="rfc-object-fields">
                                    {Object.entries(item).map(([key, val]) => (
                                        <li key={key}>
                                            <span className="rfc-object-key">{formatFieldName(key)}:</span>{' '}
                                            <span className="rfc-object-value">{formatNestedValue(val)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                );
            }
            
            return value.join(', ');
        }
        
        if (typeof value === 'object') {
            return (
                <ul className="rfc-object-fields">
                    {Object.entries(value).map(([key, val]) => (
                        <li key={key}>
                            <span className="rfc-object-key">{formatFieldName(key)}:</span>{' '}
                            <span className="rfc-object-value">{formatNestedValue(val)}</span>
                        </li>
                    ))}
                </ul>
            );
        }
        
        return String(value);
    };
    
    // Handler for field decision (approve/reject)
    const handleFieldDecision = (rfcId, fieldName, decision) => {
        setFieldDecisions(prev => ({
            ...prev,
            [rfcId]: {
                ...prev[rfcId],
                [fieldName]: {
                    decision,
                    rejection_reason: prev[rfcId]?.[fieldName]?.rejection_reason || ''
                }
            }
        }));
    };
    
    // Handler for rejection reason input
    const handleRejectionReasonChange = (rfcId, fieldName, reason) => {
        setFieldDecisions(prev => ({
            ...prev,
            [rfcId]: {
                ...prev[rfcId],
                [fieldName]: {
                    ...prev[rfcId]?.[fieldName],
                    rejection_reason: reason
                }
            }
        }));
    };
    
    // Check if all fields in an RFC have been reviewed
    const allFieldsReviewed = (rfcId, relevantChanges) => {
        const decisions = fieldDecisions[rfcId] || {};
        return Object.keys(relevantChanges).every(fieldName => {
            const decision = decisions[fieldName]?.decision;
            return decision === 'approved' || decision === 'rejected';
        });
    };
    
    // Get count of reviewed fields
    const getReviewedFieldCount = (rfcId, relevantChanges) => {
        const decisions = fieldDecisions[rfcId] || {};
        return Object.keys(relevantChanges).filter(fieldName => {
            const decision = decisions[fieldName]?.decision;
            return decision === 'approved' || decision === 'rejected';
        }).length;
    };
    
    // Check if ALL pending RFCs have been fully reviewed
    const allPendingRFCsReviewed = () => {
        const pendingRFCs = sortedRFCs.filter(rfc => rfc.status === 'pending');
        if (pendingRFCs.length === 0) return false;
        
        return pendingRFCs.every(rfc => {
            const relevantChanges = getRelevantChanges(rfc);
            return allFieldsReviewed(rfc.id, relevantChanges);
        });
    };
    
    // Get total reviewed field count across all RFCs
    const getTotalReviewProgress = () => {
        const pendingRFCs = sortedRFCs.filter(rfc => rfc.status === 'pending');
        let totalFields = 0;
        let reviewedFields = 0;
        
        pendingRFCs.forEach(rfc => {
            const relevantChanges = getRelevantChanges(rfc);
            totalFields += Object.keys(relevantChanges).length;
            reviewedFields += getReviewedFieldCount(rfc.id, relevantChanges);
        });
        
        return { reviewedFields, totalFields };
    };
    
    // Submit review for ALL pending RFCs
    const handleSubmitAllReviews = async () => {
        try {
            setIsSubmittingReview('all');
            
            const pendingRFCs = sortedRFCs.filter(rfc => rfc.status === 'pending');
            let successCount = 0;
            let failCount = 0;
            
            // Submit each RFC
            for (const rfc of pendingRFCs) {
                const decisions = fieldDecisions[rfc.id];
                if (!decisions) continue;
                
                try {
                    const response = await crmService.reviewChangeRequest(profileId, rfc.id, decisions);
                    if (response.success) {
                        successCount++;
                    } else {
                        failCount++;
                    }
                } catch (error) {
                    console.error(`Error submitting RFC ${rfc.id}:`, error);
                    failCount++;
                }
            }
            
            // Show results
            if (successCount > 0 && failCount === 0) {
                showSuccess(`Successfully reviewed ${successCount} RFC${successCount > 1 ? 's' : ''}!`, 6000);
            } else if (successCount > 0 && failCount > 0) {
                showSuccess(`Reviewed ${successCount} RFC${successCount > 1 ? 's' : ''}, ${failCount} failed.`, 6000);
            } else {
                showError(`Failed to submit reviews. Please try again.`, 6000);
            }
            
            // Clear all field decisions
            setFieldDecisions({});
            
            // Notify parent to refresh data
            if (onReviewSubmitted) {
                onReviewSubmitted();
            }
            
            // Close modal
            onClose();
            
        } catch (error) {
            console.error('Error submitting all reviews:', error);
            showError(getErrorMessage(error, 'Failed to submit reviews. Please try again.'), 6000);
        } finally {
            setIsSubmittingReview(null);
        }
    };
    
    const totalRFCCount = sortedRFCs.length;
    
    return (
        <div className="rfc-details-overlay" onClick={handleOverlayClick}>
            <div className="rfc-details-modal">
                {/* Header */}
                <div className="rfc-details-header">
                    <div className="rfc-details-header-content">
                        <h3>Request For Changes Details</h3>
                        <div className="rfc-details-profile-name">
                            {profileName} - {totalRFCCount} Pending {totalRFCCount === 1 ? 'RFC' : 'RFCs'}
                        </div>
                    </div>
                    <button 
                        className="rfc-details-close-btn" 
                        onClick={onClose}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                {/* Body - List View */}
                <div className="rfc-details-body">
                    <div className="rfc-list-container">
                        {sortedRFCs.map((rfc, index) => {
                            const isExpanded = expandedRFCs[rfc.id];
                            const relevantChanges = getRelevantChanges(rfc);
                            const relevantChangeCount = Object.keys(relevantChanges).length;
                            const originalChangeCount = Object.keys(rfc.changes_data || {}).length;
                            const hasSupersededChanges = relevantChangeCount < originalChangeCount;
                            
                            return (
                                <div key={rfc.id} className="rfc-card">
                                    {/* RFC Card Header - Collapsible */}
                                    <div 
                                        className="rfc-card-header"
                                        onClick={() => toggleRFC(rfc.id)}
                                    >
                                        <div className="rfc-card-header-left">
                                            <div className="rfc-card-toggle-icon">
                                                <FontAwesomeIcon 
                                                    icon={isExpanded ? faChevronDown : faChevronRight} 
                                                />
                                            </div>
                                            <div className="rfc-card-title">
                                                <span className="rfc-card-index">RFC #{sortedRFCs.length - index}</span>
                                                <span className="rfc-card-date">
                                                    Submitted {formatDate(rfc.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="rfc-card-header-right">
                                            <span className={`rfc-status-badge ${getStatusClass(rfc.status)}`}>
                                                <FontAwesomeIcon icon={getStatusIcon(rfc.status)} />
                                                {rfc.status ? rfc.status.charAt(0).toUpperCase() + rfc.status.slice(1) : 'Unknown'}
                                            </span>
                                            <span className="rfc-card-change-count">
                                                {relevantChangeCount} {relevantChangeCount === 1 ? 'change' : 'changes'}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* RFC Card Body - Expandable */}
                                    {isExpanded && (
                                        <div className="rfc-card-body">
                                            {/* RFC Metadata */}
                                            <div className="rfc-card-meta">
                                                <div className="rfc-meta-item">
                                                    <label>Requested By:</label>
                                                    <span>{rfc.requested_by_name || 'Unknown'}</span>
                                                </div>
                                                {rfc.requested_by_employee_id && (
                                                    <div className="rfc-meta-item">
                                                        <label>Employee ID:</label>
                                                        <span>{rfc.requested_by_employee_id}</span>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {rfc.request_notes && (
                                                <div className="rfc-card-notes">
                                                    <label>Request Notes:</label>
                                                    <p>{rfc.request_notes}</p>
                                                </div>
                                            )}
                                            
                                            {hasSupersededChanges && (
                                                <div className="rfc-superseded-notice">
                                                    <FontAwesomeIcon icon={faInfoCircle} />
                                                    <span>
                                                        {originalChangeCount - relevantChangeCount} {originalChangeCount - relevantChangeCount === 1 ? 'field' : 'fields'} 
                                                        {' '}superseded by newer RFC
                                                    </span>
                                                </div>
                                            )}
                                            
                                            {/* Changes List */}
                                            <div className="rfc-card-changes">
                                                <h5>Proposed Changes:</h5>
                                                {relevantChangeCount === 0 ? (
                                                    <div className="rfc-no-changes">
                                                        All changes in this RFC have been superseded by newer RFCs.
                                                    </div>
                                                ) : (
                                                    <div className="rfc-changes-table">
                                                        {Object.entries(relevantChanges).map(([fieldName, change]) => {
                                                            const localDecision = fieldDecisions[rfc.id]?.[fieldName];
                                                            const existingDecision = rfc.field_decisions?.[fieldName];
                                                            const decision = localDecision?.decision || existingDecision?.decision;
                                                            const rejectionReason = localDecision?.rejection_reason || existingDecision?.rejection_reason || '';
                                                            
                                                            return (
                                                                <div key={fieldName} className="rfc-change-row">
                                                                    <div className="rfc-field-name">
                                                                        {formatFieldName(fieldName)}
                                                                    </div>
                                                                    <div className="rfc-value-comparison">
                                                                        <div className="rfc-value-box rfc-old-value">
                                                                            <label>Old:</label>
                                                                            <div className="rfc-value-content">
                                                                                {formatValue(change.old, null)}
                                                                            </div>
                                                                        </div>
                                                                        <div className="rfc-arrow">
                                                                            <FontAwesomeIcon icon={faArrowRight} />
                                                                        </div>
                                                                        <div className="rfc-value-box rfc-new-value">
                                                                            <label>New:</label>
                                                                            <div className="rfc-value-content">
                                                                                {formatValue(change.new, change.old)}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    {/* CRM Approver Actions for Pending RFCs */}
                                                                    {isCRMApprover && rfc.status === 'pending' && (
                                                                        <div className="rfc-field-actions">
                                                                            <button 
                                                                                className={`rfc-approve-btn ${decision === 'approved' ? 'active' : ''}`}
                                                                                onClick={() => handleFieldDecision(rfc.id, fieldName, 'approved')}
                                                                                type="button"
                                                                            >
                                                                                <FontAwesomeIcon icon={faCheck} /> Approve
                                                                            </button>
                                                                            <button 
                                                                                className={`rfc-reject-btn ${decision === 'rejected' ? 'active' : ''}`}
                                                                                onClick={() => handleFieldDecision(rfc.id, fieldName, 'rejected')}
                                                                                type="button"
                                                                            >
                                                                                <FontAwesomeIcon icon={faBan} /> Reject
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* Rejection Reason Input (when rejected locally) */}
                                                                    {isCRMApprover && rfc.status === 'pending' && decision === 'rejected' && (
                                                                        <input 
                                                                            type="text"
                                                                            placeholder="Rejection reason (optional)"
                                                                            value={rejectionReason}
                                                                            onChange={(e) => handleRejectionReasonChange(rfc.id, fieldName, e.target.value)}
                                                                            className="rfc-rejection-reason-input"
                                                                        />
                                                                    )}
                                                                    
                                                                    {/* Display Decision Badge for Reviewed RFCs */}
                                                                    {rfc.status !== 'pending' && existingDecision && (
                                                                        <div className="rfc-field-decision-badge">
                                                                            <span className={`decision-badge ${existingDecision.decision}`}>
                                                                                <FontAwesomeIcon icon={existingDecision.decision === 'approved' ? faCheckCircle : faTimesCircle} />
                                                                                {existingDecision.decision === 'approved' ? 'Approved' : 'Rejected'}
                                                                            </span>
                                                                            {existingDecision.rejection_reason && (
                                                                                <span className="rejection-reason">Reason: {existingDecision.rejection_reason}</span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Review Info (if reviewed) */}
                                            {rfc.reviewed_by && (
                                                <div className="rfc-card-review">
                                                    <h5>Review Information:</h5>
                                                    <div className="rfc-review-details">
                                                        <div className="rfc-meta-item">
                                                            <label>Reviewed By:</label>
                                                            <span>{rfc.reviewed_by_name}</span>
                                                        </div>
                                                        <div className="rfc-meta-item">
                                                            <label>Reviewed At:</label>
                                                            <span>{formatDate(rfc.reviewed_at)}</span>
                                                        </div>
                                                    </div>
                                                    {rfc.review_notes && (
                                                        <div className="rfc-review-notes">
                                                            <label>Review Notes:</label>
                                                            <p>{rfc.review_notes}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* Single Submit Review Button (CRM Approvers only, if there are pending RFCs) */}
                    {isCRMApprover && sortedRFCs.some(rfc => rfc.status === 'pending') && (
                        <div className="rfc-global-review-submit-section">
                            <div className="review-progress-summary">
                                <span className="progress-text">
                                    Review Progress: {getTotalReviewProgress().reviewedFields} / {getTotalReviewProgress().totalFields} fields across all RFCs
                                </span>
                            </div>
                            <button
                                className="rfc-global-submit-review-btn"
                                onClick={handleSubmitAllReviews}
                                disabled={!allPendingRFCsReviewed() || isSubmittingReview === 'all'}
                                type="button"
                            >
                                {isSubmittingReview === 'all' ? (
                                    <>
                                        <span className="rfc-btn-spinner"></span>
                                        Submitting All Reviews...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        Submit All Reviews
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
                
                {/* Footer */}
                <div className="rfc-details-footer">
                    <button 
                        className="rfc-details-close-button" 
                        onClick={onClose}
                        type="button"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RFCDetailsModal;

